import type { QueryFilters, QueryKey } from '@tanstack/react-query'

/**
 * Live order events (ADR-W12): which React Query data a frame from
 * GET /api/live/stream?scope=backoffice makes stale. Pure, so the table is
 * tested on its own; hooks/useLiveEvents does the connecting and batching.
 *
 * The server only says "order X changed", "support request X changed" (or
 * "ready": reload what is on screen). Each screen then reloads through the REST
 * call it already makes, because invalidateQueries refetches the queries that
 * are mounted and only marks the rest stale.
 */

/** Every query key prefix that holds order data somewhere in the backoffice. */
export const ORDER_QUERY_PREFIXES: readonly QueryKey[] = [
  // Board columns ['orders','board',status,filters], the order drawer
  // ['orders','detail',id] and the orders list widget ['orders',…filters,page].
  ['orders'],
  // Dashboard analytics. Not under ['orders'], so listed on its own.
  ['orders-analytics'],
  // A client's orders ['client-orders',clientId,page]. Not under ['orders'] either.
  ['client-orders'],
  // The sidebar's order badge.
  ['notifications', 'merchant'],
  // Platform Dispatch: ['dispatch','list',…] and ['dispatch','detail',orderId].
  ['dispatch'],
  // Courier deliveries.
  ['courier', 'orders'],
  // Partner desk: ['partner','orders',tab] and ['partner','order',id].
  ['partner'],
]

/**
 * Help & support (docs/support.md): the requester's own requests, options and
 * unread count under ['support'], Platform › Support (inbox, threads, the
 * sidebar badge, its settings) under ['admin-support'].
 */
export const SUPPORT_QUERY_PREFIXES: readonly QueryKey[] = [['support'], ['admin-support']]

/** The ranked courier candidates for one order: ['courier-propose', orderId]. */
export const COURIER_PROPOSE_PREFIX: QueryKey = ['courier-propose']

export interface OrderEventData {
  orderId: string | null
  courierOrderId: string | null
}

/** Reads `{"orderId":"…","courierOrderId":"…"?}` from a frame's data, raw or parsed. */
export function parseOrderEventData(data: unknown): OrderEventData {
  let value: unknown = data
  if (typeof data === 'string') {
    try {
      value = JSON.parse(data)
    } catch {
      value = null
    }
  }
  const record = value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
  const text = (v: unknown) => (typeof v === 'string' && v.length > 0 ? v : null)
  return { orderId: text(record.orderId), courierOrderId: text(record.courierOrderId) }
}

/**
 * The invalidations one frame asks for.
 *
 * - `order`: every order prefix, plus that order's courier candidates.
 * - `support` (`{"requestId"}`): both support prefixes, whichever request it
 *   was — the lists, counts and badges move with any of them.
 * - `ready`: the order prefixes, all courier candidates and the support
 *   prefixes, since whatever happened while the stream was down was missed.
 * - anything else (the `: ping` comment arrives as an empty event): nothing.
 *
 * An `order` frame without a readable orderId still means "something changed",
 * so it refreshes all courier candidates instead of none.
 */
export function invalidationsFor(event: string, data: unknown): QueryFilters[] {
  if (event === 'support') return SUPPORT_QUERY_PREFIXES.map((queryKey) => ({ queryKey }))
  if (event !== 'order' && event !== 'ready') return []

  const filters: QueryFilters[] = ORDER_QUERY_PREFIXES.map((queryKey) => ({ queryKey }))
  const orderId = event === 'order' ? parseOrderEventData(data).orderId : null
  filters.push({ queryKey: orderId ? [...COURIER_PROPOSE_PREFIX, orderId] : COURIER_PROPOSE_PREFIX })
  if (event === 'ready') filters.push(...SUPPORT_QUERY_PREFIXES.map((queryKey) => ({ queryKey })))
  return filters
}

function startsWith(key: QueryKey, prefix: QueryKey): boolean {
  return (
    prefix.length <= key.length &&
    prefix.every((part, i) => JSON.stringify(part) === JSON.stringify(key[i]))
  )
}

/**
 * One batch's filters with duplicates removed, and without keys a shorter
 * prefix in the same batch already covers (['courier-propose','o1'] goes when
 * ['courier-propose'] is there). Keeps the first-seen order.
 */
export function mergeInvalidations(filters: readonly QueryFilters[]): QueryFilters[] {
  const keys: QueryKey[] = []
  const seen = new Set<string>()
  for (const filter of filters) {
    const key = filter.queryKey
    if (!key) continue
    const id = JSON.stringify(key)
    if (seen.has(id)) continue
    seen.add(id)
    keys.push(key)
  }
  return keys
    .filter((key) => !keys.some((other) => other.length < key.length && startsWith(key, other)))
    .map((queryKey) => ({ queryKey }))
}

/** Reconnect backoff after a failed or short-lived stream, in ms (before jitter). */
export const LIVE_BACKOFF_STEPS_MS = [1_000, 2_000, 5_000, 10_000, 30_000, 60_000] as const

/**
 * The wait before reconnect attempt `attempt` (0-based): 1, 2, 5, 10, 30, then
 * 60 s for every later attempt, each ±20 % so tabs that dropped together do not
 * come back together. `random` returns [0, 1), as Math.random does.
 */
export function liveBackoffDelay(attempt: number, random: () => number = Math.random): number {
  const steps = LIVE_BACKOFF_STEPS_MS
  const base = steps[Math.min(Math.max(attempt, 0), steps.length - 1)]
  return Math.round(base * (0.8 + random() * 0.4))
}
